/**
 * Onboarding Panel Content
 *
 * Renders the onboarding checklist inside the right-side panel (shared with AI assistant).
 * Replaces the old 280px sidebar with content that fits the 400px panel container.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  LinearProgress,
  SvgIcon,
  Chip,
  Skeleton,
  CircularProgress,
} from '@mui/material'

import { useCorporateWithStore } from '_stores/corporate.store'
import { useTrialStatus, useCompleteOnboardingStep } from '_hooks/use-trial-management.hooks'
import { useSupportAssistant } from '../support-assistant/support-assistant.hooks'
import { useSiteNavigation } from '_hooks/use-site-navigation'
import { trackOnboardingStepViewed } from '@hapana/shared/analytics/src/tracking-functions'
import { SidebarHeader } from '../support-assistant/components/sidebar-header'
import IconClipboardCheck from '@hapana/shared/web/styles/assets/svgs/clipboard-check.svg'
import Button from '@hapana/shared/web/components/shared/button/button.component.jsx'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { styles } from './onboarding-panel-content.styles'

interface ChecklistItem {
  checklistId: string
  stepKey: string
  stepLabel: string
  stepDescription?: string | null
  category: string
  isCompleted: boolean
  completedAt?: string | null
  sortOrder: number
}

const STEP_ROUTES: Record<string, string> = {
  location_settings: 'settings/location-settings',
  scheduler_ready: 'settings/scheduler-settings',
  retail_settings: 'settings/retail-settings',
  account_settings: 'settings/account-settings',
  team_access: 'team-access',
  first_client: 'clients',
  first_session: 'schedule',
}

const OnboardingPanelContent = () => {
  const { t } = useTranslation('core')
  const navigate = useNavigate()
  const { navigateToSite, navigateToCorporate, siteId } = useSiteNavigation()
  const isLocationLevel = !!siteId
  const { setIsOpen, setActivePanel, setActiveTab, setPrefillIssueType, setPrefillDescription } =
    useSupportAssistant()
  const { corporate } = useCorporateWithStore()
  const corporateId = corporate?.corporateBySubdomain?.corporateId

  const { data: trialData, isLoading } = useTrialStatus(corporateId)

  const progress = trialData?.onboardingProgress
  const items: ChecklistItem[] = progress?.items || []
  const totalSteps = progress?.totalSteps ?? 0
  const completedSteps = progress?.completedSteps ?? 0
  const progressPct = progress?.progressPct ?? 0
  const daysRemaining = trialData?.daysRemaining ?? null

  const {
    mutate: completeStep,
    isLoading: isCompletingStep,
    variables: completingStepVars,
  } = useCompleteOnboardingStep()

  const handleStepClick = (item: ChecklistItem) => {
    if (item.isCompleted) return

    if (corporateId) {
      trackOnboardingStepViewed({
        corporate_id: corporateId,
        step_key: item.stepKey,
      })
    }

    const route = STEP_ROUTES[item.stepKey]
    if (route) {
      navigateToSite(route)
    }
  }

  const handleCompleteStep = (e: React.MouseEvent, item: ChecklistItem) => {
    e.stopPropagation()
    if (item.isCompleted || !corporateId) return
    completeStep({ corporateId, stepKey: item.stepKey })
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // Close panel on Escape key (matches assistant sidebar behavior)
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [setIsOpen])

  if (isLoading || !trialData) {
    return (
      <Box sx={styles.panel}>
        {/* Header skeleton */}
        <Box sx={styles.skeletonHeader}>
          <Skeleton variant="circular" width={22} height={22} />
          <Skeleton variant="text" width={100} height={24} />
        </Box>

        {/* Trial info card skeleton */}
        <Box sx={styles.skeletonTrialCard}>
          <Box sx={styles.skeletonTrialCardRow}>
            <Skeleton variant="text" width={30} height={18} />
            <Skeleton variant="rounded" width={80} height={22} sx={styles.skeletonChip} />
          </Box>
          <Box sx={styles.skeletonProgressRow}>
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={30} height={18} />
          </Box>
          <Skeleton variant="rounded" width="100%" height={6} sx={styles.skeletonProgressBar} />
        </Box>

        {/* Completed summary skeleton */}
        <Box sx={styles.skeletonSummaryRow}>
          <Skeleton variant="text" width={70} height={18} />
          <Skeleton variant="text" width={24} height={18} />
        </Box>

        {/* Checklist items skeleton */}
        <Box sx={styles.skeletonChecklist}>
          {Array.from({ length: 7 }).map((_, i) => (
            <Box key={i} sx={styles.skeletonChecklistItem}>
              <Skeleton
                variant="circular"
                width={24}
                height={24}
                sx={styles.skeletonChecklistItemIcon}
              />
              <Box sx={styles.skeletonChecklistItemText}>
                <Skeleton variant="text" width={`${60 + (i % 3) * 15}%`} height={18} />
                <Skeleton variant="text" width={`${40 + (i % 2) * 20}%`} height={14} />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Upgrade button skeleton */}
        <Box sx={styles.skeletonFooter}>
          <Skeleton variant="rounded" width="100%" height={40} sx={styles.skeletonFooterButton} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={styles.panel}>
      {/* Header — matches assistant sidebar style */}
      <SidebarHeader
        title={t('onboarding.panel.setupGuide')}
        leftIcon={<SvgIcon component={IconClipboardCheck as any} sx={styles.headerIcon} />}
        onClose={handleClose}
      />

      {/* Trial info card */}
      {daysRemaining !== null && (
        <Box sx={styles.trialCard}>
          <Box sx={styles.trialCardRow}>
            <Typography typography="medium" color="gray.80">
              {t('onboarding.panel.trial')}
            </Typography>
            <Chip
              label={t('onboarding.panel.daysLeft', { count: daysRemaining ?? 0 })}
              size="small"
              sx={styles.trialChip}
            />
          </Box>
          <Box sx={styles.progressRow}>
            <Typography typography="medium" color="gray.80">
              {t('onboarding.panel.setupProgress')}
            </Typography>
            <Typography typography="medium" color="gray.80" fontWeight={600}>
              {Math.round(progressPct)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressPct} sx={styles.progressBar} />
        </Box>
      )}

      {/* Progress summary */}
      <Box sx={styles.progressSummary}>
        <Box sx={styles.progressSummaryRow}>
          <Typography typography="medium" color="gray.80">
            {t('onboarding.panel.completed')}
          </Typography>
          <Typography typography="medium" color="gray.80">
            {t('onboarding.panel.completedSteps', { completed: completedSteps, total: totalSteps })}
          </Typography>
        </Box>
      </Box>

      {/* Checklist items */}
      <Box sx={styles.checklistScroll}>
        {items
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((item) => (
            <Box
              key={item.checklistId}
              onClick={() => handleStepClick(item)}
              sx={styles.checklistItem(item.isCompleted)}
            >
              {/* Status icon — clickable to mark complete */}
              <Box
                onClick={(e) => handleCompleteStep(e, item)}
                sx={styles.statusIcon(item.isCompleted)}
              >
                {isCompletingStep && completingStepVars?.stepKey === item.stepKey ? (
                  <CircularProgress size={14} sx={styles.spinnerColor} />
                ) : item.isCompleted ? (
                  '\u2713'
                ) : (
                  <>
                    <Box component="span" className="step-number">
                      {item.sortOrder}
                    </Box>
                    <Box component="span" className="step-check" sx={styles.stepCheckHidden}>
                      {'\u2713'}
                    </Box>
                  </>
                )}
              </Box>

              {/* Step info */}
              <Box sx={styles.stepInfo}>
                <Typography
                  typography="medium"
                  color={item.isCompleted ? 'gray.56' : 'gray.100'}
                  sx={styles.stepLabel(item.isCompleted)}
                >
                  {item.stepLabel}
                </Typography>
                {item.stepDescription && (
                  <Typography
                    typography="small"
                    color="gray.56"
                    sx={styles.stepDescription(item.isCompleted)}
                  >
                    {item.stepDescription}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
      </Box>

      {/* View Plans footer — pinned to bottom */}
      <Box sx={styles.footer}>
        <Button
          variant={BUTTON_VARIANTS.CONTAINED}
          theme={BUTTON_THEME.PRIMARY}
          label={t('onboarding.panel.viewPlans')}
          onClick={() => {
            setPrefillIssueType('PLANS')
            setPrefillDescription(t('onboarding.panel.viewPlansPrefill'))
            setActivePanel('assistant')
            setActiveTab('support')
            setIsOpen(true)
          }}
          sx={styles.footerButton}
        />
      </Box>
    </Box>
  )
}

export default OnboardingPanelContent
